import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fetchCards } from "../api";
import IMAGES from "../data/imageMap";
import { useSiteContent } from "../contexts/SiteContent";

export default function Impact() {
  const { t } = useSiteContent();
  const [cards, setCards] = useState([]);

  useEffect(() => {
    fetchCards("impact").then(setCards).catch(() => setCards([]));
  }, []);

  const featured = cards.find((c) => c.featured);
  const others = cards.filter((c) => !c.featured);

  return (
    <section
      id="impact"
      data-testid="impact-section"
      className="bg-white py-16 md:py-24 lg:py-28"
    >
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="max-w-[720px]">
          <h2 className="text-[32px] md:text-[44px] font-semibold tracking-[-0.02em] leading-[1.1] text-neutral-900">
            {t("impact_intro.heading", "Impact")}
          </h2>
          <p className="mt-4 text-[16px] md:text-[17px] text-neutral-600 leading-relaxed">
            {t(
              "impact_intro.subtitle",
              "Stories from our partners and collaborators putting science-grade AI tools to work in the real world."
            )}
          </p>
        </div>

        {featured && (
          <Link
            to={featured.slug ? `/impact/${featured.slug}` : "#"}
            data-testid={`impact-featured-${featured.order}`}
            className="group block relative mt-10 overflow-hidden rounded-2xl aspect-[16/9] md:aspect-[16/8] cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_24px_56px_rgba(0,0,0,0.18)]"
          >
            <img
              src={IMAGES.impact[featured.order]}
              alt=""
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/75 via-black/35 to-transparent" />
            <div className="absolute inset-0 p-7 md:p-12 flex flex-col justify-end text-white max-w-[640px]">
              <p className="text-[12px] uppercase tracking-[0.18em] font-medium text-white/80">
                Case study
              </p>
              <h3 className="mt-3 text-[26px] md:text-[36px] font-semibold tracking-[-0.02em] leading-[1.1]">
                {featured.title}
              </h3>
              <p className="mt-3 text-[14px] md:text-[15px] text-white/90 leading-relaxed">
                {featured.blurb}
              </p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-medium">
                Read case study
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </div>
          </Link>
        )}

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
          {others.map((card) => (
            <Link
              key={card.id}
              to={card.slug ? `/impact/${card.slug}` : "#"}
              data-testid={`impact-card-${card.order}`}
              className="group flex flex-col rounded-2xl bg-white border border-neutral-200 overflow-hidden cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)]"
            >
              <div className="aspect-[4/3] w-full overflow-hidden bg-neutral-100">
                <img
                  src={IMAGES.impact[card.order]}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                />
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="text-[17px] font-semibold tracking-tight text-neutral-900 leading-snug">
                  {card.title}
                </h3>
                <p className="mt-2 text-[13px] text-neutral-600 leading-relaxed line-clamp-3">
                  {card.blurb}
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-neutral-900">
                  Read case study
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
